<template>
  <section v-if="variantList.length > 0" class="variantList-without-offers">
    <article class="variantList-without-offers__wrapper">
      <h4 class="variantList-without-offers__title" data-test="title">
        {{ title }}
      </h4>
      <ul class="variantList-without-offers__variant-list">
        <li
          v-for="variant in variantListWithName"
          :key="variant.id"
          class="variantList-without-offers__variant-list__item"
          data-test="variant-item"
        >
          <span
            v-if="isAuthenticated && isRestrictedSales(variant) && !isFrontAgri"
            class="variantList-without-offers__variant-list__item__badges"
          >
            <adn-tag
              v-if="isAuthenticated && isRestrictedSales(variant) && !isFrontAgri"
              background-color="var(--color-coop)"
              font-size="var(--font-size-xs)"
              font-weight="bold"
              text-color="white"
              shape="0.4rem"
              border-style="none"
              data-test-exclu-tech-badge
            >
              {{ i18n._('Exclu {partnerType}', { partnerType }) }}
            </adn-tag>
          </span>
          <span class="variantList-without-offers__variant-list__item__name">{{ variant.name }}</span>
        </li>
      </ul>
      <p class="variantList-without-offers__text" data-test="message">
        {{ message }}
      </p>
      <adn-button v-if="isFrontAgri" data-test="showModal" kind="secondary" @click="showModal">
        {{ i18n._('Contacter votre') }} {{ i18n._('technicien') }}
      </adn-button>
    </article>
    <p-contact-technical-sale-modal
      v-if="profile"
      ref="contactTCModal"
      class="content-block"
      :contact-list="profile.technicalSales"
    />
  </section>
</template>

<script lang="ts">
import { i18n, getAppTypeLocale } from '@b2ag/locale'
import { useUser } from '@b2ag/user/src/useUser'
import { computed, ref, defineComponent, type PropType } from 'vue'
import { currentAppBrand } from '@b2ag/app-brands'
import { useStore } from 'vuex'
import PContactTechnicalSaleModal from '@b2ag/offers/src/presenters/PContactTechnicalSaleModal/PContactTechnicalSaleModal.vue'
import type { ProductVariant } from './typings'
import AdnButton from '@b2ag/design-system/src/components/AdnButton/AdnButton.vue'
import AdnTag from '@b2ag/design-system/src/components/AdnTag/AdnTag.vue'
import { getAppContext } from '@b2ag/micro-front-tools'

export default defineComponent({
  name: 'PVariantWithoutOffer',
  components: { PContactTechnicalSaleModal, AdnButton, AdnTag },
  props: {
    variantList: {
      type: Array as PropType<ProductVariant[]>,
      default: () => [],
    },
  },
  setup(props) {
    const store = useStore()
    const { isFrontAgri } = getAppContext()
    const { profile } = useUser()
    const TITLE = {
      AGRI: i18n._(`Il n'y a actuellement aucune offre disponible pour ce produit.`),
      TECH: i18n._('Autres conditionnements possibles'),
    }

    const MESSAGE = {
      AGRI: i18n._(`Votre coopérative ou négoce n’a pas encore associé un prix pour les conditionnements ci-dessus. \
Nous vous invitons à contacter votre technicien pour avoir plus d'informations.`),
      TECH: i18n._(`Votre coopérative ou négoce n’a pas encore référencé d’offres pour les conditionnements ci-dessus. \
Nous vous invitons à contacter votre partenaire pour trouver un produit correspondant à votre besoin.`),
    }

    const variantListWithName = props.variantList.filter((variant) => variant.name)
    const contactTCModal = ref<(typeof PContactTechnicalSaleModal & { show: () => void; hide: () => void }) | null>(
      null,
    )

    const title = computed(() => (isFrontAgri ? TITLE.AGRI : TITLE.TECH))

    const message = computed(() => (isFrontAgri ? MESSAGE.AGRI : MESSAGE.TECH))

    const appTypeLocale = getAppTypeLocale(currentAppBrand)
    const partnerType = computed(() => appTypeLocale.tc)
    const isAuthenticated = computed(() => store.getters.isAuthenticated)

    const isRestrictedSales = (variant) => {
      return variant.restrictedToProfiles?.length === 1 && variant.restrictedToProfiles?.includes('sales')
    }

    const showModal = () => {
      contactTCModal.value?.show()
    }

    return {
      isFrontAgri,
      profile,
      variantListWithName,
      title,
      message,
      showModal,
      contactTCModal,
      partnerType,
      isAuthenticated,
      isRestrictedSales,
      i18n,
    }
  },
})
</script>

<style scoped lang="scss">
.variantList-without-offers {
  text-align: center;
  list-style: none;
  background: var(--color-white);
  padding: 2rem 1rem;
  border: 1px solid var(--color-inactive-light);
  border-radius: 0.8rem;

  @media screen and (min-width: $breakpoint-lg) {
    padding: 2rem 8rem;
  }

  .variantList-without-offers__wrapper {
    border: 0;
    padding: 0;
    color: var(--color-black);
    background: white;

    .variantList-without-offers__title {
      padding: 0;
      text-transform: uppercase;
      font-size: 1em;
      font-weight: bold;
      line-height: 22px;
      margin-bottom: 2rem;
    }
  }
}

.variantList-without-offers__text {
  margin-bottom: 2rem;
}

.variantList-without-offers__variant-list {
  display: flex;
  justify-content: center;
  white-space: nowrap;
  flex-wrap: wrap;
  align-items: center;
  margin-bottom: 1.5rem;
}

.variantList-without-offers__variant-list__item {
  padding: var(--spacing-2) var(--spacing-2);
  margin: var(--spacing-1);
  position: relative;
  background-color: var(--color-grey-lighter);
  border-radius: var(--border-radius-1);
  font-size: var(--font-size-m);
  line-height: var(--line-height-m);
  display: flex;
  flex-direction: row;

  @media (min-width: $breakpoint-md) {
    align-items: center;
  }

  .tag {
    padding: 0.1rem 0.8rem;
    letter-spacing: 0.04rem;
    height: 2.2rem;
    line-height: var(--line-height-s);
  }

  @media (max-width: $breakpoint-md) {
    width: 100%;
    text-align: left;
    flex-direction: column;

    &__name {
      white-space: normal;
      word-wrap: break-word;
      overflow-wrap: break-word;
    }
  }

  &__badges {
    display: flex;
    flex-direction: row;
    align-items: center;
    width: 100%;

    div {
      margin-right: var(--spacing-2);
    }

    @media (max-width: $breakpoint-md) {
      margin-bottom: var(--spacing-2);
    }
  }
}
</style>
